import { useWatch } from "react-hook-form"
import { Lens } from "@hookform/lenses"
import { BetterDocSection } from "@/modules/patients/detail/better-doc/components/better-doc-section"
import { useApplicableBetterDocUsage } from "@/modules/patients/detail/better-doc/hooks/useApplicableBetterDocUsage"
import type { BetterDocCheck } from "@/modules/patients/detail/better-doc/hooks/useBetterDocRejectionConfirmation"
import { CareProviderContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/care-provider-content"
import { DoctorVisitAvoidedContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/doctor-visit-avoided-content"
import { ReasonForChoosingType } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/reason-for-choosing-type"
import { SpecialtyContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/specialty-content"
import { TimeToTreatContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/time-to-treat-content"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type { ContentProps, HasCareProvider } from "@/modules/patients/detail/progress-note/point-of-care/types"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"

type HospitalElectiveContentProps = ContentProps &
  HasCareProvider & {
    pointOfCareFormLens: Lens<PointOfCareFormType>
    episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
    diagnosisCode: string
    setCareProvider: (careProvider: string | null) => void
    onBetterDocCheck: BetterDocCheck
  }

export const HospitalElectiveContent = (props: HospitalElectiveContentProps) => {
  const {
    pointOfCareFormLens,
    isActual,
    timeToTreatData,
    countryCode,
    hasCareProvider,
    episodeOfCareProgressNoteData,
    diagnosisCode,
    setCareProvider,
    onBetterDocCheck,
  } = props

  const specialtyLens = pointOfCareFormLens.focus("specialtyId")
  const timeToTreatLens = pointOfCareFormLens.focus("timeToTreatType")
  const specialty = useWatch(specialtyLens.interop())
  const timeToTreatType = useWatch(timeToTreatLens.interop())

  const applicableBetterDocUsage = useApplicableBetterDocUsage({
    episodeOfCareProgressNoteData,
    diagnosisCode,
    effectivePointOfCare: "HOSPITAL_ELECTIVE",
    countryCode,
    setCareProvider,
  })

  return (
    <div className="col-span-2 gap-4">
      {isActual && (
        <ReasonForChoosingType reasonForChoosingTypeLens={pointOfCareFormLens.focus("reasonForChoosingType")} />
      )}
      <div className="flex flex-col gap-4 pt-4 @lg/patient-layout:flex-row">
        <TimeToTreatContent timeToTreatLens={timeToTreatLens} timeToTreatData={timeToTreatData} />
        <SpecialtyContent specialtyLens={specialtyLens} />
      </div>
      <BetterDocSection
        applicableBetterDocUsage={applicableBetterDocUsage}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
      />
      {countryCode === "CH" && (
        <div className="flex flex-row gap-4 pt-4">
          <CareProviderContent
            pointOfCareFormLens={pointOfCareFormLens}
            searchType={specialty ? "HOSPITAL_ELECTIVE_WITH_SPECIALTY" : "HOSPITAL_ELECTIVE_WITHOUT_SPECIALTY"}
            specialtyId={specialty?.specialty?.id}
            hasCareProvider={hasCareProvider}
            timeToTreat={timeToTreatType ?? undefined}
            useSpecialty
            allowUnknownCareProvider
            episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
            applicableBetterDocUsage={applicableBetterDocUsage}
            onBetterDocCheck={(callback, event) => onBetterDocCheck(applicableBetterDocUsage, callback, event)}
          />
        </div>
      )}
      <DoctorVisitAvoidedContent isDoctorVisitAvoidedLens={pointOfCareFormLens.focus("isDoctorVisitAvoided")} />
    </div>
  )
}
